interface PortalHelpContentProps {
  /** Agency contact email to pre-fill the "Email your agency" mailto. */
  agencyContactEmail?: string;
  /** Agency display name, used in the email subject + intro line. */
  agencyName?: string;
}

/**
 * Help content shown when a portal contact opens the help drawer.
 * Goal: answer "what am I looking at?" and give a one-tap way to reach
 * the agency if they're still stuck.
 */
export function PortalHelpContent({ agencyContactEmail, agencyName }: PortalHelpContentProps) {
  const mailtoSubject = encodeURIComponent('Question about our Scope workspace');
  const mailtoHref = agencyContactEmail
    ? `mailto:${agencyContactEmail}?subject=${mailtoSubject}`
    : null;
  const them = agencyName ?? 'your agency';

  return (
    <div className="space-y-6 text-scope-navy">
      <section>
        <h3 className="mb-2 text-sm font-semibold">How approvals work</h3>
        <p className="text-xs leading-relaxed text-scope-text-muted">
          When {them} finishes a piece of work and is ready for you to sign off, it shows up in{' '}
          <strong>Approvals</strong>. Tap the row, review what they've delivered, and either
          <strong> Approve</strong> (locks it in) or <strong>Request revision</strong> (kicks it
          back with your notes). Either way, {them} sees it instantly.
        </p>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">How invoices work</h3>
        <p className="text-xs leading-relaxed text-scope-text-muted">
          The <strong>Invoices</strong> screen shows what's outstanding (sent, not paid yet) and
          what's been paid. Amounts are shown ex VAT in the app; the invoice PDF itself shows
          inclusive figures.
        </p>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">The value summary</h3>
        <p className="text-xs leading-relaxed text-scope-text-muted">
          The big numbers on the dashboard:
        </p>
        <ul className="mt-2 list-disc space-y-1.5 pl-5 text-xs leading-relaxed text-scope-text-muted">
          <li>
            <strong>Value delivered</strong> — the total quoted value of every piece of work {them}{' '}
            has finished on this engagement.
          </li>
          <li>
            <strong>Retainer paid</strong> — what you've actually paid {them}.
          </li>
          <li>
            <strong>Value vs retainer</strong> — delivered ÷ paid. Above 1× means you're getting
            more than you pay for.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">Proposing a new project</h3>
        <p className="text-xs leading-relaxed text-scope-text-muted">
          Got something new for {them}? Tap <strong>Propose a project</strong> in the top bar,
          describe what you want, and submit. They'll see it in their inbox and respond here.
        </p>
      </section>

      <section className="rounded-xl border border-scope-border-soft bg-scope-cream-warm/40 p-4">
        <h3 className="mb-1 text-sm font-semibold">Need a human?</h3>
        {mailtoHref ? (
          <>
            <p className="text-xs leading-relaxed text-scope-text-muted">
              Anything not covered here, ask {them} directly.
            </p>
            <a
              href={mailtoHref}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-scope-navy px-3 py-2 text-xs font-medium text-white transition hover:bg-scope-navy-soft"
            >
              Email {them}
            </a>
          </>
        ) : (
          <p className="text-xs leading-relaxed text-scope-text-muted">
            Anything not covered here, reach out to {them} directly.
          </p>
        )}
      </section>
    </div>
  );
}
